export type SaveState = "idle" | "dirty" | "saving" | "saved" | "local" | "conflict" | "error" | "recovery";

export interface SaveAttempt<T> {
  mutationId: string;
  expectedRevision: number;
  value: T;
}

export interface EditRecord<T> {
  value: T;
  baseRevision: number;
  baseValue?:T;
  conflicted?:boolean;
  attempt?: SaveAttempt<T>;
}

export interface DraftView<T> {
  value: T;
  revision: number;
  status: SaveState;
  message: string;
}

interface Options<T> {
  value: T;
  revision: number;
  send: (attempt: SaveAttempt<T>) => Promise<{ revision: number }>;
  persist?: (record: EditRecord<T> | null) => Promise<void>;
  onSaved?: (value: T, revision: number) => void;
  validate?: (value: T) => void;
  isConflict: (error: unknown) => boolean;
  /** A definitive validation rejection was not committed; edited content may use a new key. */
  isRejected?: (error: unknown) => boolean;
  /** Connection/session failures can wait for sync, but only after durable local persistence. */
  deferredMessage?: (error: unknown) => string | undefined;
}

/** One writer per resource. Retried requests keep their original key and payload. */
export class RevisionDraft<T> {
  private view: DraftView<T>;
  private saved: string;
  private attempt: SaveAttempt<T> | undefined;
  private running: Promise<boolean> | undefined;
  private localWrite: Promise<void> = Promise.resolve();
  private hasLocalRecord = false;
  private conflicted = false;
  private observedRemoteRevision: number;
  private listeners = new Set<() => void>();

  constructor(private options: Options<T>) {
    this.view = { value: options.value, revision: options.revision, status: "idle", message: "" };
    this.saved = JSON.stringify(options.value);
    this.observedRemoteRevision = options.revision;
  }

  getSnapshot = (): DraftView<T> => this.view;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify(patch: Partial<DraftView<T>>): void {
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  private persist(): Promise<void> {
    const record = this.conflicted || this.attempt || JSON.stringify(this.view.value) !== this.saved
      ? { value: this.view.value, baseRevision: this.view.revision, baseValue:JSON.parse(this.saved) as T,conflicted:this.conflicted, ...(this.attempt ? { attempt: this.attempt } : {}) }
      : null;
    // Capture now, not when the queued write eventually runs.
    const copy = record ? structuredClone(record) : null;
    this.localWrite = this.localWrite.catch(() => undefined).then(async() => {
      await this.options.persist?.(copy);
      this.hasLocalRecord = copy !== null && !!this.options.persist;
    });
    return this.localWrite;
  }

  restore(record: EditRecord<T>): void {
    this.hasLocalRecord=!!this.options.persist;
    this.attempt = record.attempt;
    if(record.baseValue!==undefined)this.saved=JSON.stringify(record.baseValue);
    const conflict = record.conflicted===true||(!record.attempt && record.baseRevision !== this.view.revision);
    this.conflicted = conflict;
    this.notify({
      value: record.value,
      revision: record.baseRevision,
      status: conflict ? "conflict" : "dirty",
      message: conflict ? "本机有尚未同步的修改，另一台设备也已修改此内容。你的输入已保留。" : ""
    });
  }

  getBaseValue():T {return JSON.parse(this.saved) as T;}
  async resolveWith(value:T,remote:T,revision:number):Promise<void>{
    if(this.running)await this.running;
    if(revision<this.view.revision)throw new Error("远端版本已过期，请重新读取。");
    if(revision<this.observedRemoteRevision)throw new Error("已有更新的远端版本，请重新读取后合并。");
    this.attempt=undefined;this.conflicted=false;this.observedRemoteRevision=revision;this.saved=JSON.stringify(remote);
    this.notify({value,revision,status:JSON.stringify(value)===this.saved?"saved":"dirty",message:""});
    await this.persist();
  }
  change(value: T): void {
    this.notify({
      value,
      status: this.conflicted ? "conflict" : "dirty",
      message: this.conflicted ? this.view.message : ""
    });
    void this.persist().catch(() => {
      this.notify({ status: this.conflicted?"conflict":"error", message: "本机保存失败，请保留当前页面并重试。" });
    });
  }

  /** A saved AI adoption may update a clean editor; concurrent local input stays intact. */
  receiveRemote(value:T,revision:number):boolean {
    if(revision<=this.view.revision)return false;
    this.observedRemoteRevision=Math.max(this.observedRemoteRevision,revision);
    if(this.attempt||this.running||JSON.stringify(this.view.value)!==this.saved){
      this.conflicted=true;this.notify({status:"conflict",message:"内容已有新版本，你当前的输入已保留，请核对后再保存。"});
      void this.persist().catch(()=>this.notify({status:"conflict",message:"新版本与本机修改冲突，且本机存储失败，请保留页面并备份输入。"}));return false;
    }
    this.saved=JSON.stringify(value);this.conflicted=false;this.notify({value,revision,status:"saved",message:""});void this.persist().catch(()=>this.notify({status:"error",message:"本机缓存更新失败，请保留页面并重试。"}));return true;
  }

  flush = (): Promise<boolean> => {
    if (this.running) return this.running;
    // A clean editor has no server or local draft work to finish. In
    // particular, do not wait for a queued IndexedDB cleanup transaction
    // during chapter/reference navigation; that queue is not user input.
    if (this.canLeaveImmediately()) return Promise.resolve(true);
    this.running = this.drain().finally(() => { this.running = undefined; });
    return this.running;
  };

  /** Navigation needs a durable draft, even when syncing requires author review. */
  prepareToLeave = async (): Promise<boolean> => {
    if (this.canLeaveImmediately()) return true;
    if (await this.flush()) return true;
    try { await this.persist(); return true; }
    catch {
      this.notify({ message: "本机保存失败，请保留当前页面并重试。" });
      return false;
    }
  };

  private canLeaveImmediately(): boolean {
    return !this.conflicted && !this.attempt &&
      (this.view.status === "idle" || this.view.status === "saved");
  }

  private async drain(): Promise<boolean> {
    if (this.conflicted) return false;
    for (;;) {
      if (this.conflicted) return false;
      if (!this.attempt && JSON.stringify(this.view.value) === this.saved) {
        try {
          await this.localWrite;
          if(this.hasLocalRecord)await this.persist();
        }
        catch {
          try { await this.persist(); }
          catch {
            this.notify({ status: "error", message: "本机保存失败，请保留当前页面并重试。" });
            return false;
          }
        }
        // Typing may continue while IndexedDB is acknowledging cleanup.
        if (this.conflicted || this.attempt || JSON.stringify(this.view.value) !== this.saved) continue;
        this.notify({ status: this.view.status === "idle" ? "idle" : "saved", message: "" });
        return true;
      }
      this.notify({ status: "saving", message: "" });
      try {
        if (!this.attempt) {
          this.options.validate?.(this.view.value);
          this.attempt = {
            mutationId: crypto.randomUUID(),
            expectedRevision: this.view.revision,
            value: structuredClone(this.view.value)
          };
        }
        const attempt = this.attempt;
        await this.persist();
        const result = await this.options.send(attempt);
        this.saved = JSON.stringify(attempt.value);
        this.attempt = undefined;
        this.notify({ revision: result.revision });
        if(result.revision>=this.observedRemoteRevision){
          this.observedRemoteRevision=result.revision;
          this.conflicted=false;
          this.options.onSaved?.(attempt.value, result.revision);
        }else{
          this.conflicted=true;
          this.notify({status:"conflict",message:"这次保存已成功，但服务器已有更新版本。本机稿已保留，请核对后再继续。"});
        }
        await this.persist();
      } catch (error) {
        if(this.options.isRejected?.(error))this.attempt=undefined;
        this.conflicted = this.conflicted || this.options.isConflict(error);
        let locallySaved=false,storageFailed=false;
        try {
          let pending=this.persist();
          // Include input added while an earlier IndexedDB write was completing.
          for(;;){await pending;if(pending===this.localWrite)break;pending=this.localWrite;}
          locallySaved=!!this.options.persist;
        } catch {storageFailed=true;}
        const deferred=locallySaved?this.options.deferredMessage?.(error):undefined;
        this.notify({
          status: this.conflicted ? "conflict" : deferred ? "local" : "error",
          message: storageFailed ? "本机保存失败，请保留当前页面并重试。" : this.conflicted
            ? "内容在另一处已修改。你的输入已保留，请核对后再保存。"
            : deferred ?? (error instanceof Error ? error.message : "保存失败，请重试。")
        });
        return false;
      }
    }
  }
}
